import React from 'react';
import PropTypes from 'prop-types';

import Drawer from 'relements/build/esm/overlays/drawer';
import Icon from 'relements/build/esm/ui/icon';
import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';
import Loader from 'relements/build/esm/ui/loader';
import WithTooltip from 'relements/build/esm/overlays/withTooltip';

import connection from '@src/connection';
import { GOOGLE_ANALYTICS } from '@src/const';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import { AlertTypes } from '@src/constants/MogamboConstants';
import DebugWindow from '@src/containers/DebugWindow';

// Components
import ErrorBoundary from '@src/components/ErrorBoundary';

// Store
import BotStore from '@src/store';

import * as maps from './maps';

import Refresh from './icons/refresh.svg';
import styles from './TestBot.scss';

const SDK_LOADER_URL =
  'https://toolassets.haptikapi.com/platform/javascript-xdk/production/loader.js';

/**
 * TestBot Container
 * Responsible for rendering the test bot on the left hand side inside a drawer
 * It automatically fetched the credentials for the current bot and inits
 * the SDK
 * @type {Object}
 */
@connection(maps.listenToStores, maps.getStateFromStores)
class TestBot extends React.Component {
  constructor() {
    super();

    this.state = {
      loading: false,
      credentials: undefined,
      debugActive: false,
      messageId: '',
      domainId: '',
      error: '',
    };
  }

  /**
   * On Mount we fetch the SDK credentials for the current domain (bot)
   * It's async because we're using await
   * @return {Promise} [description]
   */
  async componentDidMount() {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    if (!hasSelfServeLayout) {
      const credentials = (await maps.getSDKCredentials(this.props.domain.id))
        .body;
      this.setState({ credentials, domainId: this.props.domain.id });
    }
  }

  async componentDidUpdate() {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    if (
      hasSelfServeLayout &&
      this.props.active &&
      this.props.domain.id !== this.state.domainId
    ) {
      const testConfigErr = BotStore.getTestConfigErr();
      const testConfigData = BotStore.getTestConfigData();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ domainId: this.props.domain.id, error: testConfigErr });
      if (Object.keys(testConfigData).length !== 0) {
        this._refreshSDK();
      } else {
        this.props.onClose();
      }
    }
  }

  /**
   * Renders the drawer inside which we render an iFrame with the SDK
   * @return {[type]} [description]
   */
  render() {
    const { credentials, debugActive } = this.state;
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    const testConfigData = BotStore.getTestConfigData();
    const testConfig = hasSelfServeLayout ? testConfigData : credentials;
    // For ww as per design the width of sidebar is 320, so to match decreasing testbot width
    const drawerWidth = hasSelfServeLayout ? 320 : 360;

    return (
      <>
        <ErrorBoundary fallback={<span>Unable to Open</span>}>
          <Drawer
            active={this.props.active}
            position="LEFT"
            width={drawerWidth}
            zIndex={5}
            noOverlay
            noDisable
            offset={hasSelfServeLayout ? 24 : 0}
            attachTo="app-body"
            prefixClassName={`${hasSelfServeLayout ? 'testBot_ww' : ''}`}
          >
            {this._renderHeader()}
            <div className={styles.iframeWrapper}>
              {this.state.loading && (
                <Box
                  alignItems="center"
                  height="100%"
                  width="100%"
                  style={{
                    background: '#ffffff',
                  }}
                >
                  <Loader themed />
                </Box>
              )}
              {this.state.error && (
                <Box
                  alignItems="center"
                  height="100%"
                  width="auto"
                  style={{
                    background: '#ffffff',
                    textAlign: 'center',
                    position: 'absolute',
                    padding: '0 2.5rem',
                  }}
                >
                  <Text>{this.state.error}</Text>
                </Box>
              )}
              {testConfig &&
                this._renderSDK(
                  testConfig?.business_id,
                  testConfig?.partner_client_id,
                  testConfig?.base_url
                    ?.replace('.com/', '.com')
                    ?.replace('http:', 'https:'),
                  testConfig?.partner_api_key
                )}
            </div>
          </Drawer>
          <DebugWindow
            active={this.props.active && debugActive}
            messageId={this.state.messageId}
            onClose={this._closeDebugModal}
          />
        </ErrorBoundary>
      </>
    );
  }

  _renderHeader() {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    return (
      <div
        className={`${styles.testBotHeader}  ${
          hasSelfServeLayout ? styles.testBotHeader_ww_noHeader : ''
        }`}
      >
        <div className={styles.testBotHeaderTitle}>Test Bot</div>
        <div className={styles.testBotHeaderIconWrapper}>
          <WithTooltip
            tooltip="Refresh chat"
            tooltipPosition="BOTTOM"
            className={styles.testBotHeaderIconToolTip}
          >
            <Icon
              onClick={this._refreshSDK}
              className={styles.testBotHeaderIcon}
              src={Refresh}
            />
          </WithTooltip>
          <WithTooltip tooltip="Close chat" tooltipPosition="BOTTOM">
            <Icon
              onClick={this.props.onClose}
              className={styles.testBotHeaderIcon}
              src="closeV2"
            />
          </WithTooltip>
        </div>
      </div>
    );
  }

  /**
   * Renders the SDK inside an iframe with the right credentials
   * @param  {[type]} businessId [description]
   * @param  {[type]} clientId   [description]
   * @param  {[type]} baseURL    [description]
   * @param  {[type]} apiKey     [description]
   * @return {[type]}            [description]
   */
  _renderSDK(businessId, clientId, baseURL, apiKey) {
    return (
      <iframe
        id="haptik-sdk-test-bot"
        title="haptik-sdk"
        className={styles.iframe}
        ref={(iframe) => {
          if (!this._iframe) {
            this._iframe = iframe;
            this._loadIframeContents(businessId, clientId, baseURL, apiKey);
          }
        }}
      />
    );
  }

  /**
   * This function calls the HaptikSDK.destroy() and clears local storage
   * to start a new chat.
   *
   * We get the HaptikSDK functions via iFrame with id -> haptik-sdk-test-bot
   *
   */
  _refreshSDK = () => {
    const { credentials } = this.state;
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    const testConfigData = BotStore.getTestConfigData();
    const testConfig = hasSelfServeLayout ? testConfigData : credentials;

    this.setState({
      debugActive: false,
      messageId: undefined,
      loading: true,
    });
    try {
      document
        .getElementById('haptik-sdk-test-bot')
        .contentWindow.HaptikSDK.destroy();

      const currIVA = localStorage.getItem('ww_iva');
      const currEnv = localStorage.getItem('ww_env');
      document
        .getElementById('haptik-sdk-test-bot')
        .contentWindow.localStorage.clear();

      localStorage.setItem('ww_iva', currIVA);
      localStorage.setItem('ww_env', currEnv);

      this._loadIframeContents(
        testConfig?.business_id,
        testConfig?.partner_client_id,
        testConfig?.base_url
          ?.replace('.com/', '.com')
          ?.replace('http:', 'https:'),
        testConfig?.partner_api_key
      );
    } catch (e) {
      this.setState({ loading: false });
      maps.createAlert(
        'Unable to refresh! Try again.',
        null,
        AlertTypes.NEGATIVE
      );
    }
  };

  /**
   * Once the iframe is loaded, we write into the iframe document with the
   * right loader HTML for the javascript.
   * We must use document.write as we're adding script tags and css which must
   * be loaded by the iFrame
   * @param  {[type]} businessId [description]
   * @param  {[type]} clientId   [description]
   * @param  {[type]} apiKey     [description]
   * @param  {[type]} baseURL    [description]
   * @return {[type]}            [description]
   */
  _loadIframeContents = (businessId, clientId, baseURL, apiKey) => {
    const frame =
      this._iframe.contentWindow ||
      this._iframe.contentDocument.document ||
      this._iframe.contentDocument;
    frame.document.open();
    frame.document.write(`
            <html>
                <head>
                    <style>
                        iframe {
                            width: 100%;
                            height: 100%;
                            border: none !important;
                        }
                        body {
                            margin: 0;
                        }
                    </style>
                </head>
                <body>
                    <div id="debug-section"></div>
                    <script>
                        var haptikInitSettings = {
                          'business-id': ${businessId},
                          'client-id': '${clientId}',
                          'api-key': '${apiKey}',
                          'base-url': '${baseURL}',
                          'mode': 'fsm',
                          'signup-type': 'guest',
                          'debug': true,
                          'debug-section-dom-function': 'getDebugDOM'
                        };
                    </script>
                    <script
                        type="text/javascript"
                        charset="UTF-8"
                        src="${SDK_LOADER_URL}"
                    ></script>
                </body>
        `);
    frame.document.close();
    frame.getDebugDOM = () => {
      return {
        setMessageId: this._setMessageId,
        onOpen: this._openDebugModal,
      };
    };

    setTimeout(() => {
      this.setState({ loading: false });
    }, 1000);
  };

  /**
   * Close the debug drawer
   * @return {[type]} [description]
   */
  _closeDebugModal = () => {
    this.setState({ debugActive: false });
    if (this.closeCallback) this.closeCallback();
  };

  /**
   * Open the debug drawer
   * @return {[type]} [description]
   */
  _openDebugModal = (cb) => {
    this.setState({ debugActive: true });
    this.closeCallback = cb;
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.TEST,
      GOOGLE_ANALYTICS.EVENTS.TESTCHAT_DEBUG,
      this.props.domain.title,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  /**
   * Sets message ID for debug logs
   * @param {(string|number)} messageId id of the message
   */
  _setMessageId = (messageId) => {
    this.setState({ messageId });
  };
}

TestBot.propTypes = {
  domain: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  active: PropTypes.bool,
  onClose: PropTypes.func,
};

TestBot.defaultProps = {
  domain: {
    id: '',
    title: '',
  },
  active: false,
  onClose: () => null,
};

export default TestBot;
